// A wrapper for <Route> that redirects to the login

import React from "react";
import { Redirect, Route } from "react-router-dom";
import { useSelector } from "react-redux";

import { CONNECTING, ONBOARDING } from "../../navigation/CONSTANTS";
import {
  getWizardOnboardingRedirect,
  isWizardOnboardingIncomplete,
} from "../../utils/onboardingStepsUtils";

/*
 * Same overlay Forecasting / Incoming Inventory already use while data loads.
 * Shown in-place so the admin URL stays on the page the merchant clicked
 * (e.g. /ui/ or /ui/settings) instead of bouncing to /ui/connecting.
 */
function AuthLoadingFallback() {
  return <div id="cover-spin" />;
}

// screen if you're not yet authenticated.
function PrivateRoute({ children, ...rest }) {
  const { isLoggedIn, onboardingStepsStatus } = useSelector((state) => state.auth);

  return (
    <Route
      {...rest}
      render={({ location }) => {
        // Session token is still being checked (including after an App Bridge
        // nav remount). Keep this URL and show the cover-spin loader.
        if (isLoggedIn === null) {
          return <AuthLoadingFallback />;
        }

        // No install record. OAuth is about to start. Connecting is outside
        // PageLayout so Help is not flashed or highlighted in admin nav.
        if (isLoggedIn === false) {
          // Keep Shopify's ?shop=&host= query string. Redirecting to pathname
          // alone used to drop it, so /install built https://null/admin/oauth/...
          // Example: /ui/?shop=my-shop.myshopify.com&host=... → /ui/connecting?shop=my-shop.myshopify.com&host=...
          return (
            <Redirect
              to={{
                pathname: CONNECTING,
                search: location.search,
                hash: location.hash,
                state: { from: location },
              }}
            />
          );
        }

        // Logged in, but wizard status has not come back yet. Stay on cover-spin
        // so Forecast Report is not rendered (and leftover nav cannot skip the wizard).
        if (onboardingStepsStatus === null) {
          return <AuthLoadingFallback />;
        }

        const onOnboardingPath = location.pathname.startsWith(ONBOARDING);
        if (
          isWizardOnboardingIncomplete(onboardingStepsStatus) &&
          !onOnboardingPath
        ) {
          const redirect = getWizardOnboardingRedirect(onboardingStepsStatus);
          if (redirect) {
            return <Redirect to={redirect} />;
          }
        }

        return <>{children}</>;
      }}
    />
  );
}
export default PrivateRoute;

/**
 * Below code can be organized in separate files as desired.
 * I am keeping everything related to the authentication and PrivateRoute in this one file.
 */
/*
const authContext = createContext();
export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return (
    <authContext.Provider value={auth}>
      {children}
    </authContext.Provider>
  );
}

// Most important hook to be used throughout
export function useAuth() {
  return useContext(authContext);
}
*/

/*
function useProvideAuth() {
  const [user, setUser] = useState(null);

  // signin method: It can either return a promise or execute a callback function.
  // You can prefer to keep this in userServices.js
  const signin = () => {
    console.log("SS:: PrivateRoute > useProviderAuth > signin() called...");
    return new Promise((resolve, reject) => {
      try {
        // do db call or API endpoint axios call here and return the promise.
        let apiResponse = {
          "id": "30",
          "firstName": "Joel",
          "lastName": "Joseph",
          "gender": "Male",
          "age": 33,
          "isActiveEmployee": true,
          "location": "London"
        };
        setUser(apiResponse);
        setTimeout(()=>resolve(apiResponse), 3000);
        // resolve(apiResponse);
      } catch (error) {
        console.error("signin error!==", error);
        reject("signin error!");
      }
    });
  };
  const signout = () => {
    return new Promise((resolve, reject) => {
      try {
        // do API endpoint axios call here and return the promise.
        setUser(null);
        resolve(true);
      } catch (error) {
        console.error("signout error!==", error);
        reject("signout error!");
      }
    });
  };

  // Firebase auth example code (ref: https://usehooks.com/useAuth/)
  // const signin = (email, password) => {
  //   return firebase
  //     .auth()
  //     .signInWithEmailAndPassword(email, password)
  //     .then(response => {
  //       setUser(response.user);
  //       return response.user;
  //     });
  // };
  // const signout = () => {
  //   return firebase
  //     .auth()
  //     .signOut()
  //     .then(() => {
  //       setUser(false);
  //     });
  // };
  
  return {
    user,
    signin,
    signout
  };
}
*/
